// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  SETTINGSTAB.JSX - VERSION: 2025.12.24.1115                                ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.12.24.1115
// ⚙️ Settings Tab - Profile & Preferences
// ============================================================================
// CHANGELOG:
// 2025.12.13.1430 - Added proper links to Help, Terms, Privacy, GDPR pages
// 2025.12.03.0001 - Fixed Picture Grid toggle (was using wrong localStorage key), default to enabled
// 2025.12.02.1820 - Initial version
// ============================================================================
import React, { useState, useEffect } from 'react';

const SettingsTab = ({ 
  playerData, 
  onUpdateProfile,
  onUpdateHandedness,
  notifications = [],
  onMarkNotificationRead,
  pictureGridSettings,
  onUpdatePictureGridSettings
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  
  const unreadCount = notifications.filter(n => !n.read).length;
  
  // Picture Grid enabled state - uses localStorage directly
  // Default to TRUE (enabled) if not set
  const [pictureGridEnabled, setPictureGridEnabled] = useState(() => {
    const saved = localStorage.getItem('pictureGridEnabled');
    // If never set, default to true (enabled)
    if (saved === null) {
      localStorage.setItem('pictureGridEnabled', 'true');
      return true;
    }
    return saved === 'true';
  });
  
  // Toggle picture grid enabled/disabled
  const handleTogglePictureGrid = () => {
    const newValue = !pictureGridEnabled;
    setPictureGridEnabled(newValue);
    localStorage.setItem('pictureGridEnabled', newValue.toString());
    
    // Also update the pictureGridSettings for consistency
    if (onUpdatePictureGridSettings && pictureGridSettings) {
      onUpdatePictureGridSettings({
        ...pictureGridSettings,
        enabled: newValue
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 pb-20">
      <div className="max-w-md mx-auto px-4 py-4">
        
        {/* Header */}
        <h1 className="text-white text-2xl font-bold mb-4 flex items-center gap-2">
          <span>⚙️</span> Settings
        </h1>

        {/* Profile Card */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-3xl">
              {playerData?.avatar || '👤'}
            </div>
            <div>
              <p className="text-white font-bold text-lg">{playerData?.username || 'Player'}</p>
              <p className="text-gray-400 text-sm">ID: #{playerData?.playerId || '0000'}</p>
            </div>
          </div>
          <div className="text-gray-400 text-sm">
            <p>📧 {playerData?.email || 'No email set'}</p>
            <p>📱 {playerData?.countryCode} {playerData?.phoneNumber || 'No phone set'}</p>
          </div>
        </div>

        {/* Notifications */}
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          className="w-full bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔔</span>
            <span className="text-white font-bold">Notifications</span>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                {unreadCount}
              </span>
            )}
            <span className="text-gray-400">{showNotifications ? '▼' : '▶'}</span>
          </div>
        </button>

        {showNotifications && (
          <div className="bg-gray-800/50 rounded-xl p-3 mb-4 max-h-60 overflow-y-auto">
            {notifications.length > 0 ? notifications.map(notif => (
              <div 
                key={notif.id}
                onClick={() => onMarkNotificationRead && onMarkNotificationRead(notif.id)}
                className={`p-3 rounded-lg mb-2 cursor-pointer ${
                  notif.read ? 'bg-gray-700/50' : 'bg-blue-700/50 border border-blue-500'
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <p className={`font-bold text-sm ${notif.read ? 'text-gray-300' : 'text-white'}`}>
                    {notif.subject}
                  </p>
                  {!notif.read && <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">New</span>}
                </div>
                <p className="text-gray-400 text-xs">{notif.message}</p>
              </div>
            )) : (
              <p className="text-gray-500 text-center py-4">No notifications</p>
            )}
          </div>
        )}

        {/* Handedness */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>✋</span> Handedness
          </h3>
          <p className="text-gray-400 text-sm mb-3">
            Choose which hand you prefer for button placement
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => onUpdateHandedness && onUpdateHandedness('left')}
              className={`p-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                playerData?.handedness === 'left'
                  ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <span className="text-xl">🫲</span>
              <span className="font-bold">Left</span>
            </button>
            <button
              onClick={() => onUpdateHandedness && onUpdateHandedness('right')}
              className={`p-3 rounded-xl flex items-center justify-center gap-2 transition-all ${
                playerData?.handedness === 'right'
                  ? 'bg-gradient-to-r from-blue-500 to-teal-500 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              <span className="text-xl">🫱</span>
              <span className="font-bold">Right</span>
            </button>
          </div>
        </div>

        {/* Picture Grid Toggle */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>🖼️</span> Picture Grid
          </h3>
          <p className="text-gray-400 text-sm mb-3">
            Show picture grid bonus game between questions
          </p>
          <button
            onClick={handleTogglePictureGrid}
            className={`w-full p-3 rounded-xl flex items-center justify-between transition-all ${
              pictureGridEnabled
                ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                : 'bg-gray-700'
            }`}
          >
            <span className="text-white font-bold">
              {pictureGridEnabled ? '✓ Picture Grid Enabled' : '✗ Picture Grid Disabled'}
            </span>
            <div className={`w-12 h-6 rounded-full flex items-center p-1 transition-all ${
              pictureGridEnabled ? 'bg-green-700' : 'bg-gray-600'
            }`}>
              <div className={`w-4 h-4 bg-white rounded-full transition-all ${
                pictureGridEnabled ? 'ml-auto' : 'ml-0'
              }`} />
            </div>
          </button>
        </div>

        {/* Sound Settings (Placeholder) */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>🔊</span> Sound
          </h3>
          <div className="flex items-center justify-between">
            <span className="text-gray-300">Sound Effects</span>
            <div className="w-12 h-6 bg-green-500 rounded-full flex items-center p-1">
              <div className="w-4 h-4 bg-white rounded-full ml-auto" />
            </div>
          </div>
        </div>

        {/* App Info */}
        <div className="bg-gray-800 rounded-xl p-4 mb-4 border border-gray-700">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <span>ℹ️</span> About
          </h3>
          <div className="space-y-2 text-sm text-gray-400">
            <p>TRAKtag Quiz v2025.12.13</p>
            <p>The Ultimate Quiz Game</p>
            <div className="flex gap-4 mt-3 flex-wrap">
              <a 
                href="/help" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-yellow-400 hover:text-yellow-300 font-bold"
              >
                📖 How to Play
              </a>
            </div>
            <div className="flex gap-4 mt-2 flex-wrap">
              <a 
                href="/terms" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                Terms
              </a>
              <a 
                href="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                Privacy
              </a>
              <a 
                href="/gdpr" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300"
              >
                GDPR
              </a>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-gray-800 rounded-xl p-4 border border-red-500/30">
          <h3 className="text-red-400 font-bold mb-3 flex items-center gap-2">
            <span>⚠️</span> Danger Zone
          </h3>
          <button className="text-red-400 text-sm hover:text-red-300">
            Reset All Progress
          </button>
        </div>

      </div>
    </div>
  );
};

export default SettingsTab;
